import React, { useEffect } from "react";
import { X } from "lucide-react";

const END_PRACTICE_STEPS = [
  {
    title: "Regulate",
    description:
      "You checked in with your feelings and helped your brain and body settle.",
    imageSrc: "images/brain/brain1.jpg",
    imageAlt: "Regulate brain step",
  },
  {
    title: "Relate",
    description:
      "You used your senses to connect with the present moment and what is around you.",
    imageSrc: "images/brain/brain2.jpg",
    imageAlt: "Relate brain step",
  },
  {
    title: "Reason",
    description:
      "You chose a wellness practice that helps your brain get ready to learn and grow.",
    imageSrc: "images/brain/brain3.jpg",
    imageAlt: "Reason brain step",
  },
];

export default function EndPracticeBrainModal({
  isOpen,
  steps = END_PRACTICE_STEPS,
  showDoneButton = false,
  onClose,
  onDone,
  variant = "dialog",
}) {
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, variant]);

  if (!isOpen) {
    return null;
  }

  const cardContent = (
    <div className="flex w-full flex-col items-center gap-4">
      <img
        src="images/brain/3colorRRR.gif"
        alt="Brain animation"
        className="h-auto w-40 flex-shrink-0 sm:w-56 border-t border-b border-[#DDDDDD]"
      />

      <h2 className="mx-4 text-center text-2xl font-bold">You primed your brain for success!</h2>

      <div className="flex w-full border-l-4 border-[#3387CF] flex-col gap-4 rounded bg-[#F7FAFD] px-4 py-4">
        {steps.map((step) => (
          <div key={step.title} className="flex items-start gap-4">
            <img
              src={step.imageSrc}
              alt={step.imageAlt}
              className="h-20 w-18 flex-shrink-0 rounded object-cover"
            />
            <div className="flex flex-col gap-1 pt-1">
              <h3 className="text-base font-semibold text-[#111827]">
                {step.title}
              </h3>
              <p className="text-sm text-[#4B5563]">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (variant === "hovercard") {
    return (
      <div
        className="fixed inset-0 z-[1000] sm:ml-[130px] flex items-center justify-center bg-black/30 p-4 sm:items-center sm:p-6"
        onClick={handleBackdropClick}
        role="presentation"
      >
        <div
          className="relative flex max-h-[70vh] w-full max-w-[90vw] sm:max-w-[80%] sm:max-h-[60%] lg:max-w-[60%] lg:max-h-[80%] 2xl:max-h-[800px] 2xl:max-w-[730px] flex-col overflow-hidden rounded bg-white shadow-[0_20px_60px_rgba(18,45,70,0.22)]"
          role="dialog"
          aria-modal="true"
          aria-label="Brain success details"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF4FF] text-[#3387CF] transition hover:bg-[#D8EBFF]"
            aria-label="Close brain modal"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="overflow-y-auto p-4 sm:p-6">
            {cardContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[1000] sm:ml-[130px] flex items-center justify-center bg-black/30 p-4 sm:items-center sm:p-6"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="relative flex max-h-[70vh] w-full max-w-[90vw] sm:max-w-[80%] sm:max-h-[60%] lg:max-w-[60%] lg:max-h-[80%] 2xl:max-h-[800px] 2xl:max-w-[730px] flex-col overflow-hidden rounded bg-white shadow-[0_20px_60px_rgba(18,45,70,0.22)]"
        role="dialog"
        aria-modal="true"
        aria-label="Brain success details"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF4FF] text-[#3387CF] transition hover:bg-[#D8EBFF]"
          aria-label="Close brain modal"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="overflow-y-auto p-4 sm:p-6">
          {cardContent}
        </div>
        {showDoneButton && (
          <div className="flex justify-center px-4 py-4 sm:pb-6 sm:px-6">
            <button
              type="button"
              className="rounded border-0 bg-[#3387CF] px-4 py-2 text-base font-semibold text-white"
              onClick={onDone}
            >
              Great, I&apos;m done!
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
